import React, {
  useEffect,
  useRef,
} from 'react';

import {
  Box,
  useTheme,
} from '@mui/material';

import styles from './styles';

interface IVoiceWaveform {
  getInputByteFrequencyData: () => Uint8Array;
  isActive: boolean;
}

interface IWaveformRenderOptions {
  barColor: string;
  canvas: HTMLCanvasElement;
  frequencyData: Uint8Array | null;
  smoothedValues: number[];
}

const BAR_GAP = 3;
const BAR_HEIGHT = 4;
const BAR_RADIUS = 2;
const BAR_WIDTH = 3;
const FADE_WIDTH = 18;
const FREQUENCY_MAX_VALUE = 255;
const WAVEFORM_HEIGHT_RATIO = 0.8;
const WAVEFORM_UPDATE_INTERVAL = 50;

const getCenterWeight = (barIndex: number, barCount: number) => {
  if (barCount <= 1) {
    return 1;
  }

  const centerIndex = (barCount - 1) / 2;
  return 1 - (Math.abs(barIndex - centerIndex) / centerIndex);
};

const getIdleValue = (barIndex: number, barCount: number) =>
  0.18 + (getCenterWeight(barIndex, barCount) * 0.1);

const getFrequencyAmplitude = (
  frequencyData: Uint8Array,
  barIndex: number,
  barCount: number,
) => {
  const sourceBarCount = Math.ceil(barCount / 2);
  const mirroredIndex = barIndex < sourceBarCount
    ? barIndex
    : barCount - barIndex - 1;
  const bucketStart = Math.floor(
    (mirroredIndex / sourceBarCount) * frequencyData.length,
  );

  if (bucketStart >= frequencyData.length) {
    return 0;
  }

  const bucketEnd = Math.min(
    frequencyData.length,
    Math.max(
      bucketStart + 1,
      Math.floor(
        ((mirroredIndex + 1) / sourceBarCount) * frequencyData.length,
      ),
    ),
  );
  let bucketTotal = 0;
  let bucketPeak = 0;

  for (
    let frequencyIndex = bucketStart;
    frequencyIndex < bucketEnd;
    frequencyIndex += 1
  ) {
    const frequencyValue = frequencyData[frequencyIndex];
    bucketTotal += frequencyValue;
    bucketPeak = Math.max(bucketPeak, frequencyValue);
  }

  const bucketSize = bucketEnd - bucketStart;
  const averageAmplitude = bucketTotal / (bucketSize * FREQUENCY_MAX_VALUE);
  const peakAmplitude = bucketPeak / FREQUENCY_MAX_VALUE;

  return (averageAmplitude * 0.6) + (peakAmplitude * 0.4);
};

const getTargetValue = (
  frequencyData: Uint8Array | null,
  barIndex: number,
  barCount: number,
) => {
  const idleValue = getIdleValue(barIndex, barCount);

  if (!frequencyData?.length) {
    return idleValue;
  }

  const amplitude = getFrequencyAmplitude(
    frequencyData,
    barIndex,
    barCount,
  );

  return Math.min(1, idleValue + (amplitude * 1.8));
};

const drawRoundedBar = (
  context: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
) => {
  const radius = Math.min(BAR_RADIUS, width / 2, height / 2);

  context.beginPath();
  context.moveTo(x + radius, y);
  context.lineTo(x + width - radius, y);
  context.quadraticCurveTo(x + width, y, x + width, y + radius);
  context.lineTo(x + width, y + height - radius);
  context.quadraticCurveTo(
    x + width,
    y + height,
    x + width - radius,
    y + height,
  );
  context.lineTo(x + radius, y + height);
  context.quadraticCurveTo(x, y + height, x, y + height - radius);
  context.lineTo(x, y + radius);
  context.quadraticCurveTo(x, y, x + radius, y);
  context.closePath();
  context.fill();
};

const drawEdgeFade = (
  context: CanvasRenderingContext2D,
  width: number,
  height: number,
) => {
  if (width <= 0) {
    return;
  }

  const gradient = context.createLinearGradient(0, 0, width, 0);
  const fadePercent = Math.min(0.2, FADE_WIDTH / width);

  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(fadePercent, 'rgba(255,255,255,0)');
  gradient.addColorStop(1 - fadePercent, 'rgba(255,255,255,0)');
  gradient.addColorStop(1, 'rgba(255,255,255,1)');

  context.globalCompositeOperation = 'destination-out';
  context.fillStyle = gradient;
  context.fillRect(0, 0, width, height);
  context.globalCompositeOperation = 'source-over';
};

const renderWaveform = ({
  barColor,
  canvas,
  frequencyData,
  smoothedValues,
}: IWaveformRenderOptions) => {
  const context = canvas.getContext('2d');

  if (!context) {
    return;
  }

  const { height, width } = canvas.getBoundingClientRect();
  context.clearRect(0, 0, width, height);

  const barStep = BAR_WIDTH + BAR_GAP;
  const barCount = Math.max(1, Math.floor(width / barStep));
  const waveformWidth = (barCount * barStep) - BAR_GAP;
  const horizontalOffset = Math.max(0, (width - waveformWidth) / 2);
  const centerY = height / 2;

  smoothedValues.length = barCount;

  for (let barIndex = 0; barIndex < barCount; barIndex += 1) {
    const targetValue = getTargetValue(
      frequencyData,
      barIndex,
      barCount,
    );
    const previousValue = smoothedValues[barIndex] ?? targetValue;
    const value = frequencyData
      ? (previousValue * 0.5) + (targetValue * 0.5)
      : targetValue;
    const renderedBarHeight = Math.max(
      BAR_HEIGHT,
      value * height * WAVEFORM_HEIGHT_RATIO,
    );
    const x = horizontalOffset + (barIndex * barStep);
    const y = centerY - (renderedBarHeight / 2);

    smoothedValues[barIndex] = value;
    context.fillStyle = barColor;
    context.globalAlpha = 0.3 + (value * 0.7);
    drawRoundedBar(
      context,
      x,
      y,
      BAR_WIDTH,
      renderedBarHeight,
    );
  }

  context.globalAlpha = 1;
  drawEdgeFade(context, width, height);
};

const resizeCanvas = (
  canvas: HTMLCanvasElement,
  container: HTMLDivElement,
) => {
  const { height, width } = container.getBoundingClientRect();
  const pixelRatio = window.devicePixelRatio || 1;
  const scaledHeight = Math.round(height * pixelRatio);
  const scaledWidth = Math.round(width * pixelRatio);

  if (canvas.height !== scaledHeight || canvas.width !== scaledWidth) {
    canvas.height = scaledHeight;
    canvas.width = scaledWidth;
    canvas.style.height = `${height}px`;
    canvas.style.width = `${width}px`;
  }

  canvas
    .getContext('2d')
    ?.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
};

const VoiceWaveform = ({
  getInputByteFrequencyData,
  isActive,
}: IVoiceWaveform) => {
  const theme = useTheme();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const smoothedValuesRef = useRef<number[]>([]);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;

    if (!canvas || !container) {
      return undefined;
    }

    let animationFrame: number | null = null;
    let lastUpdateAt = 0;
    let latestFrequencyData: Uint8Array | null = null;
    let resizeObserver: ResizeObserver | null = null;

    smoothedValuesRef.current = [];

    const drawCurrentWaveform = () => {
      renderWaveform({
        barColor: theme.palette.primary.main,
        canvas,
        frequencyData: latestFrequencyData,
        smoothedValues: smoothedValuesRef.current,
      });
    };

    const handleResize = () => {
      resizeCanvas(canvas, container);
      drawCurrentWaveform();
    };

    handleResize();

    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(handleResize);
      resizeObserver.observe(container);
    } else {
      window.addEventListener('resize', handleResize);
    }

    const prefersReducedMotion = window
      .matchMedia?.('(prefers-reduced-motion: reduce)')
      .matches ?? false;

    const updateWaveform = (timestamp: number) => {
      if (timestamp - lastUpdateAt >= WAVEFORM_UPDATE_INTERVAL) {
        lastUpdateAt = timestamp;

        try {
          latestFrequencyData = getInputByteFrequencyData();
        } catch {
          latestFrequencyData = null;
          smoothedValuesRef.current = [];
        }

        drawCurrentWaveform();
      }

      animationFrame = window.requestAnimationFrame(updateWaveform);
    };

    if (isActive && !prefersReducedMotion) {
      animationFrame = window.requestAnimationFrame(updateWaveform);
    }

    return () => {
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }

      resizeObserver?.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [
    getInputByteFrequencyData,
    isActive,
    theme.palette.primary.main,
  ]);

  return (
    <Box
      ref={containerRef}
      aria-hidden="true"
      sx={styles.voiceWaveform}
    >
      <Box
        component="canvas"
        ref={canvasRef}
        aria-hidden="true"
        sx={styles.voiceWaveCanvas}
      />
    </Box>
  );
};

export default VoiceWaveform;
